import { z } from 'zod';
import { defineStepType } from '../contract.js';
import { guidanceDocumentSchema } from '../../requirements/compliance.js';
import { fileCategorySchema } from '../../files/categories.js';
import { CALENDAR_DATE } from '../../time/agency-dates.js';
/**
 * The person uploads a file, HR approves it, and the file is committed to protected storage (ADR-009).
 *
 * Note what approving produces: routing the file, minting a credential when the
 * requirement carries an expiry, and recomputing the rollup. None of that is called
 * directly — the step returns a description of what should happen and the queue does it
 * (PLAN §6 rule 2).
 */
/**
 * Hoisted so `readyToSubmit` can parse a draft with the same shape the submission uses,
 * rather than a second hand-written check that has to agree with it.
 */
const documentUploadSubmission = z.object({
    uploadId: z.string().min(1),
    fileName: z.string().min(1),
    /**
     * Nullable WITH a default, and the default is load-bearing history: `runtime.approve`
     * re-parses stored submissions, so tightening this would reject records already approved.
     * Whether a blank is ACCEPTABLE is decided by `collectsExpiry` in `checkSubmission`.
     */
    expiresAt: z.string().datetime().nullable().default(null),
});
/**
 * "I don't have this ready right now" (#232, Julio 2026-09-17: uploads never block onboarding).
 *
 * A real answer the person may SEND, so a missing document never holds up the rest of the
 * packet: the step leaves the person's list and the packet can be sealed. It is evidence of
 * nothing. `refuseApproval` stops HR approving it, and readiness reads it as still waiting on
 * the person (`effectiveStepState`), so the requirement stays outstanding until a real file is
 * approved. HR then sends a secure upload link for this same step, which reopens it.
 *
 * Strict and literal, so it cannot be mistaken for, or smuggled into, an upload.
 */
export const documentUploadNotReadySchema = z.object({ notReady: z.literal(true) }).strict();
/** The one submission shape, both answers. The upload is tried first. */
export const documentUploadSubmissionSchema = z.union([documentUploadSubmission, documentUploadNotReadySchema]);
/** True for a stored or drafted "not ready" answer. Anything else — including nothing — is false. */
export function isNotReadyUpload(value) {
    return documentUploadNotReadySchema.safeParse(value).success;
}
/**
 * The state readiness reads a step AS. Derived, never stored (hard rule 4).
 *
 * A "not ready" answer is a submission that evidences nothing, so the step reads as still with
 * the person — `sent` — whether it is waiting for HR to send a link (`submitted`) or HR has sent
 * one (`rejected`, the reopen). It never reads as awaiting review, and never as satisfied.
 * Every other step reads as its own state.
 */
export function evidenceStepState(instance) {
    if ((instance.state === 'submitted' || instance.state === 'rejected') && isNotReadyUpload(instance.submission))
        return 'sent';
    return instance.state;
}
/** True while a "not ready" answer is waiting for HR to send the person an upload link. */
export function awaitingUploadLink(instance) {
    return instance.state === 'submitted' && isNotReadyUpload(instance.submission);
}
/**
 * The approved wording (06b forms 13–18, UIUX-FIGMA-HANDOFF "Uploads never block onboarding";
 * HR review 4.1). Shared so the portal, HR's checklist and the tests say the same words.
 */
export const NOT_READY_UPLOAD_WORDING = {
    option: "I don't have this ready right now",
    note: 'You can still finish onboarding. HR will send you a secure upload link for it later.',
    hrStatus: 'Not ready — request later',
    hrAction: 'Send upload link',
    /** The person's own list of what they sent, for this answer. */
    listed: 'not ready yet. HR will send you a secure upload link for it later.',
    /** What the person sees on the step once HR has asked again. */
    reopened: 'HR has asked for this document. Upload it here when you have it.',
};
export const documentUpload = defineStepType({
    type: 'document-upload',
    /** The person supplies the file. Nobody else can. */
    submittedBy: 'person',
    label: 'Document upload',
    description: 'Collects a file from the person and stores it in the record once HR approves.',
    configSchema: z.object({
        heading: z.string().min(1),
        instructions: z.string().default(''),
        documents: z.array(guidanceDocumentSchema).max(10).optional(),
        acceptedTypes: z.array(z.string().min(1)).min(1),
        /**
         * CEILING MATCHED TO WHAT THE SERVER WILL SERVE (#239). It was 50 against a 20 MB serving
         * cap, so a published config could accept a 40 MB upload that is then refused at download
         * with an over-cap alarm — stored, filed, and unreadable. The two numbers have to move
         * together; this one may not be raised without raising the serving cap first.
         */
        maxSizeMb: z.number().int().positive().max(20).default(20),
        /**
         * Where the approved file is filed. Closed vocabulary — see files/categories.ts.
         * Whether it is segregated follows from this and is never configured separately.
         */
        target: fileCategorySchema,
        /** Set when the document also establishes a tracked credential. */
        requirementKey: z.string().min(1).nullable().default(null),
        collectsExpiry: z.boolean().default(false),
        /**
         * When HR would like it by, as a calendar date in the agency zone — set on a one-off
         * request (#62), never on a catalog step. OPTIONAL WITHOUT A DEFAULT on purpose: a default
         * would add the key to every seeded definition's validated config, move its content hash,
         * and open a new version of every catalog upload on the next seed.
         */
        dueDate: z.string().regex(CALENDAR_DATE).optional(),
    }),
    submissionSchema: documentUploadSubmissionSchema,
    /**
     * The button must not turn on for a submission the server will refuse.
     *
     * The DEFAULT rule — parse the draft against `submissionSchema` — says yes to a draft with
     * no expiry at all, because `.default(null)` MANUFACTURES one. That is the whole of #120:
     * `collectsExpiry: true` is read everywhere as "the submission carries an expiry", and the
     * default quietly disagreed. Both sides typechecked, so nothing caught it.
     *
     * The schema keeps its default rather than becoming config-dependent, deliberately:
     * `runtime.approve` re-parses the STORED submission, so a schema that refused a null
     * expiry would make every already-approved dateless record un-reapprovable. The rule that
     * varies with config belongs here and in `checkSubmission`, not in the shape.
     */
    readyToSubmit: ({ config, draft }) => {
        if (isNotReadyUpload(draft))
            return true;
        const parsed = documentUploadSubmission.safeParse(draft);
        if (!parsed.success)
            return false;
        return !(config.collectsExpiry && parsed.data.expiresAt === null);
    },
    /**
     * The server's own gate, in the person's words.
     *
     * `readyToSubmit` only decides whether a button is enabled. This is what refuses a body
     * that arrives anyway, and it names the field — a person who has done everything asked of
     * them must never be left on "Needs review" with nothing on screen saying which answer
     * would fix it.
     */
    checkSubmission: async ({ config, submission }) => {
        // Not ready carries no document, so there is no printed date to ask for.
        if ('notReady' in submission)
            return { ok: true };
        if (config.collectsExpiry && submission.expiresAt === null) {
            return { ok: false, message: 'Please enter the expiry date shown on this document.' };
        }
        return { ok: true };
    },
    refuseApproval: ({ submission }) => 'notReady' in submission
        ? 'The person said this document is not ready yet, so there is nothing to approve. Send them an upload link instead.'
        : null,
    /** The document, when one was sent. `notReady` is an answer that names none. */
    uploads: ({ submission }) => ('notReady' in submission ? [] : [submission.uploadId]),
    onApprove: ({ instance, config, submission }) => 'notReady' in submission
        // Unreachable: `refuseApproval` refuses this answer before `onApprove` is called. Written
        // out rather than asserted so the type narrows, and so a regression approves NOTHING.
        ? []
        : [
            {
                kind: 'store-file',
                instanceId: instance.id,
                uploadId: submission.uploadId,
                category: config.target,
                source: 'portal-upload',
                // Pinned at enqueue, where the upload is in hand; a step type has no upload to plan for.
                plan: null,
            },
            ...(config.requirementKey
                ? [
                    {
                        kind: 'mint-credential',
                        personId: instance.personId,
                        requirementKey: config.requirementKey,
                        identifier: null,
                        issuedAt: null,
                        expiresAt: submission.expiresAt,
                    },
                ]
                : []),
            { kind: 'recompute-rollup', personId: instance.personId },
        ],
    expiresAt: ({ config, submission }) => ('notReady' in submission || !config.collectsExpiry ? null : submission.expiresAt),
});
//# sourceMappingURL=document-upload.js.map